import { useState, type ReactNode } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type {
    ActiveHandBack,
    FinalReviewSection,
    HandBackSource
} from '../../../../services/finalReview';
import { notesInSection } from './handBackNotes';
import { useFinalReviewHandBack } from './useFinalReviewHandBack';
import { CARD_TEXT_CLASS, VersionCard } from './VersionCard';

const CHIP_TONES = {
    review: 'bg-yellow-100 text-yellow-800',
    design: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
    dx: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200'
} as const;

interface NoteChipProps {
    children: ReactNode;
    tone?: keyof typeof CHIP_TONES;
}

export function NoteChip({ children, tone = 'review' }: NoteChipProps) {
    return (
        <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded ${CHIP_TONES[tone]} text-[10px] font-bold uppercase tracking-wider flex-shrink-0`}
        >
            {children}
        </span>
    );
}

const SOURCE_COPY: Record<HandBackSource, { chip: string; card: string }> = {
    'final-review': { chip: 'Returned from final review', card: 'Final review feedback' },
    orthodontist: { chip: 'Returned by the orthodontist', card: 'Orthodontist feedback' }
};

export function HandBackChip({ patientId }: { patientId: string }) {
    const handBack = useFinalReviewHandBack(patientId);
    return handBack ? <NoteChip>{SOURCE_COPY[handBack.source].chip}</NoteChip> : null;
}

interface HandBackFeedbackCardProps {
    handBack: ActiveHandBack | null;
    section: FinalReviewSection;
    className?: string;
}

export function HandBackFeedbackCard({ handBack, section, className }: HandBackFeedbackCardProps) {
    const [expanded, setExpanded] = useState(true);
    const notes = handBack ? notesInSection(handBack.notes, section) : [];
    if (!handBack || notes.length === 0) return null;

    const Chevron = expanded ? ChevronUpIcon : ChevronDownIcon;
    const label = SOURCE_COPY[handBack.source].card;
    const toggle = (
        <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label.toLowerCase()}`}
            className="p-0.5 rounded text-gray-500 hover:text-gray-900 hover:bg-yellow-100 transition-colors flex-shrink-0"
        >
            <Chevron className="w-4 h-4" />
        </button>
    );

    return (
        <div className={className}>
            <VersionCard
                label={label}
                author={handBack.byName ?? handBack.by}
                at={handBack.at}
                attention
                action={toggle}
            >
                {expanded && (
                    <ul className="max-h-48 overflow-y-auto flex flex-col divide-y divide-yellow-200">
                        {notes.map((note, i) => (
                            <li
                                key={`${note.section}-${i}`}
                                className={`${CARD_TEXT_CLASS} whitespace-pre-wrap py-2 first:pt-0 last:pb-0`}
                            >
                                {note.text}
                            </li>
                        ))}
                    </ul>
                )}
            </VersionCard>
        </div>
    );
}
